import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetFootprintingCropPrimaryDataById,
  useGetFootprintingCropTillagesByPrimaryDataId,
  useGetPracticeTillages,
  useKeyedFormList,
  useMutateFootprintingCropPrimaryData,
  useMutateFootprintingCropTillages,
  useTranslation,
} from "utils";

import { PRACTICE_SLUG, ZERO_UUID } from "models";

import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  KlimRadioGroup,
  Option,
  PageSpin,
  Row,
  Select,
  Tooltip,
  Typography,
} from "components";

interface CropTillageFormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
}

interface AppliedPractice {
  id?: string;
  practiceId: string;
  implementedOnHa: number | null;
  tillageDepth?: number | null;
}

const blankPractice: AppliedPractice = {
  id: undefined,
  practiceId: "",
  implementedOnHa: null,
  tillageDepth: null,
};

const CropTillageForm = ({ primaryDataId, readOnly, farmId, cropId, year }: CropTillageFormProps) => {
  const { t } = useTranslation(["footprintingCrop", "practices"]);
  const formRef = useRef<FormInstance>(null);

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  // Form elements
  const [tillageActive, setTillageActive] = useState<boolean | null>(null);

  // GET Queries
  const tillagePractices = useGetPracticeTillages({ enabled: true });
  const tillagesQuery = useGetFootprintingCropTillagesByPrimaryDataId(primaryDataId);
  const isLoading = [cropPrimaryDataQuery, tillagePractices, tillagesQuery].some(queryIsLoading);
  const keyedTillages = useKeyedFormList<AppliedPractice>(formRef, { isReady: !isLoading });

  // Mutations
  const { mutate: mutateTillages } = useMutateFootprintingCropTillages(
    farmId,
    cropId,
    year,
    keyedTillages.entries
      .filter((t) => !!t.practiceId)
      .map((t) => ({
        id: t.id,
        footprintingFarmCropPrimaryDataId: primaryDataId ?? "",
        practicesTillageId: t.practiceId,
        implementedOnHa: t.implementedOnHa ?? null,
        tillageDepth: t.tillageDepth ?? null,
      })),
    primaryDataId ?? ""
  );

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      tillageActive,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateTillages();
        mutateCropPrimaryData();
      }
    },
    [isLoading, mutateCropPrimaryData, mutateTillages, readOnly]
  );

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (tillagesQuery.data) {
      keyedTillages.actions.hydrate(
        tillagesQuery.data.length
          ? tillagesQuery.data.map((t) => ({
              id: t.id,
              practiceId: t.practicesTillageId,
              implementedOnHa: t.implementedOnHa,
              tillageDepth: t.tillageDepth,
            }))
          : [blankPractice]
      );
    }
  }, [tillagesQuery.data, keyedTillages.actions]);

  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess) {
      setTillageActive(cropPrimaryDataQuery.data.tillageActive ?? null);

      formRef.current?.setFieldsValue({
        tillageActive: cropPrimaryDataQuery.data.tillageActive ?? null,
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = (["tillageActive"] as const).some((field) => cropPrimaryDataQuery.data?.[field] !== null);

      if (fieldsToValidate) {
        keyedTillages.actions.requestRehydrationValidation();
      }
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess, keyedTillages.actions]);

  if (primaryDataId && isLoading) return <PageSpin />;

  const tillagesSelectGroupName = "footprinting-crop-tillages-select";

  return (
    <Form dataTestId="footprinting-crop-tillage-form" ref={formRef}>
      <Card border className="padding-100">
        <Flex align="center" gap={16} justify="space-between" className="padding-100">
          <Typography variant="paragraph-m" style={{ maxWidth: "60%" }}>
            {t("cropTillageForm.tillage.question")}
            {true && <span className="required-asterisk">*</span>}
            <Tooltip title={t("cropTillageForm.tooltips.questionTillage")}>
              <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
            </Tooltip>
          </Typography>
          <KlimRadioGroup
            required
            name="tillageActive"
            dataTestId="footprinting-crop-tillage-active-radio"
            disabled={readOnly}
            vertical={false}
            type="tertiary"
            size="large"
            onChange={({ target }) => {
              setTillageActive(target.value);
            }}
            value={tillageActive}
            defaultValue={tillageActive}
            optionType="button"
            options={[true, false].map((opt) => ({
              label: t(`footprinting:radioAnswer.${opt}`),
              value: opt,
            }))}
          />
        </Flex>
        {tillageActive &&
          keyedTillages.entries.map((tillage, index) => {
            const groupWithIndex = `${tillagesSelectGroupName}-${index}`;
            const groupWithKey = `${tillagesSelectGroupName}-${tillage.clientKey}`;

            return (
              <Form.Item key={tillage.clientKey} className="padding-100">
                <Flex vertical gap={16}>
                  <Flex justify="space-between" vertical={false}>
                    <Typography variant="h5" style={{ flex: 1 }}>
                      {t("cropTillageForm.tillage.count") + (index + 1)}
                    </Typography>
                    <IconButton
                      type="tertiary"
                      dataTestId={`${groupWithIndex}-delete`}
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => {
                        keyedTillages.actions.remove(tillage.clientKey);
                        if (keyedTillages.entries.length === 1) setTillageActive(null);
                      }}
                      disabled={readOnly}
                    />
                  </Flex>
                  <Row>
                    <Col xs={28} sm={14}>
                      <Select
                        disabled={readOnly}
                        name={groupWithKey}
                        dataTestId={groupWithIndex}
                        label={t("cropTillageForm.tillage.type")}
                        tooltip={t("cropTillageForm.tooltips.tillageType")}
                        showSearch
                        rules={[{ message: "" }]}
                        initialValue={tillage.practiceId}
                        value={tillage.practiceId}
                        onChange={(v) => keyedTillages.actions.update(tillage.clientKey, { practiceId: v })}
                        dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                      >
                        {(tillagePractices.data ?? [])
                          .filter((practice) => practice.id !== ZERO_UUID)
                          .map((tillage) => (
                            <Option
                              disabled={readOnly}
                              key={`${groupWithIndex}-${tillage.id}`}
                              data-testid={`${groupWithIndex}-${tillage.id}`}
                              label={t(`soil.soilTypeOptions.${tillage}`)}
                              value={tillage.id}
                            >
                              {t(`${PRACTICE_SLUG.TILLAGES}.${tillage.translationSlug}`, { ns: "practices" })}
                            </Option>
                          ))}
                      </Select>
                    </Col>
                    <Col xs={20} sm={10} className="padding-0 padding-left-50">
                      <InputNumber
                        disabled={readOnly}
                        name={`${groupWithKey}-coverage`}
                        dataTestId={`${groupWithIndex}-coverage`}
                        label={t("cropTillageForm.question.implementedOn")}
                        tooltip={t("cropTillageForm.tooltips.implementTillageOn")}
                        suffix="ha"
                        value={tillage.implementedOnHa}
                        initialValue={tillage.implementedOnHa}
                        onChange={(v) => keyedTillages.actions.update(tillage.clientKey, { implementedOnHa: v })}
                        validateFirst
                        precision={2}
                        rules={[
                          { required: true, message: t("cropTillageForm.error.validNumber") },
                          { type: "number" },
                          {
                            validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject()),
                            message: t("cropTillageForm.error.validNumber"),
                          },
                        ]}
                      />
                    </Col>
                    <Col xs={48} sm={24} className="padding-0 padding-left-50 padding-top-50">
                      <InputNumber
                        disabled={readOnly}
                        name={`${groupWithKey}-tillageDepth`}
                        dataTestId={`${groupWithIndex}-tillageDepth`}
                        label={t("cropTillageForm.tillage.depth")}
                        tooltip={t("cropTillageForm.tooltips.tillageDepth")}
                        suffix="cms"
                        value={tillage.tillageDepth}
                        initialValue={tillage.tillageDepth}
                        onChange={(v) => keyedTillages.actions.update(tillage.clientKey, { tillageDepth: v })}
                        validateFirst
                        precision={2}
                        rules={[
                          { required: true, type: "number", message: t("cropTillageForm.error.validNumber") },
                          {
                            validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject()),
                            message: t("cropTillageForm.error.validNumber"),
                          },
                        ]}
                      />
                    </Col>
                  </Row>
                </Flex>
              </Form.Item>
            );
          })}
        {tillageActive && (
          <Button
            type="text"
            onClick={() => {
              keyedTillages.actions.add(blankPractice);
            }}
          >
            <Icon iconName="klim/plus" className="icon inline" /> {t("cropTillageForm.tillage.add")}
          </Button>
        )}
      </Card>
    </Form>
  );
};

CropTillageForm.Key = "crop.tillage";
export { CropTillageForm };
